import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { FormEvent, ReactElement } from 'react';
import api from '@/api';
import { ArchiveSidebar } from '@/archive/ArchiveSidebar';
import { nextArchivePage, PAGE_SIZE, PostArchive } from '@/archive/PostArchive';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

/**
 * The Student Tutorials archive.
 *
 * Oldest first, as legacy orders the tutorial archives, the reverse of the
 * fullapp list.
 */
function StudentTutorialsArchive(): ReactElement {
  const navigate = useNavigate();
  const { search_term } = Route.useSearch();
  // The interface language, which is part of the query key. Unlike legacy
  // there's no language prefix in the URL.
  const language = localeToApiLanguage(apiLanguage());

  const query = api.listStudentTutorials.useInfiniteQuery(
    { query: { language, search: search_term || undefined, limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // Wrapped rather than passed by reference: an annotated `lastPage`
      // would be inferred as the operation's `TQueryFnData` and collapse the
      // page type to the helper's structural one.
      getNextPageParam: (lastPage, allPages) =>
        nextArchivePage(lastPage, allPages),
    },
  );

  // Legacy's search widget searches whatever archive it's drawn on, so
  // searching navigates back to this route.
  const handleSearch = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const term = new FormData(e.currentTarget).get('search_term');
    void navigate({
      to: '/student-tutorials/',
      search: { search_term: term ? `${term}` : undefined },
    });
  };

  return (
    <PostArchive
      query={query}
      basePath="/student-tutorials"
      emptyTestId="student-tutorials-empty"
      searchTerm={search_term}
      onSearch={handleSearch}
      sidebar={
        <ArchiveSidebar
          sidebar="student-tutorials-sidebar"
          language={language}
          postTo="/student-tutorials/$slug"
          searchTerm={search_term}
          onSearch={handleSearch}
        />
      }
    />
  );
}

export const Route = createFileRoute('/_app/student-tutorials/')({
  component: StudentTutorialsArchive,
  // Legacy's parameter name.
  validateSearch: (
    search: Record<string, unknown>,
  ): { search_term?: string } => ({
    search_term: search.search_term ? `${search.search_term}` : undefined,
  }),
});
